import IMask from "imask";
import { type ComponentProps, useEffect, useRef } from "react";
import { type Control, type FieldPath, useController } from "react-hook-form";
import type { FieldValues } from "react-hook-form/dist/types/fields";
import { FormGroup } from "@/components/form/FormGroup";
import { Input } from "@/components/form/Input";

interface NumberInputProps<TFieldValues extends FieldValues>
  extends Omit<ComponentProps<typeof Input>, "name" | "value" | "onChange"> {
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
  label: string;
  required?: boolean;
  dp?: number; // Decimal-precision
}

export function NumberInput<TFieldValues extends FieldValues>(
  props: NumberInputProps<TFieldValues>,
) {
  const {
    error,
    className,
    name,
    control,
    label,
    required = false,
    dp = 2,
    ...rest
  } = props;
  const innerRef = useRef<HTMLInputElement>(null);
  const maskRef = useRef<ReturnType<typeof IMask>>(null);
  const {
    fieldState,
    field: { value, onChange, ref: fieldRef, ...register },
  } = useController({ name, control });

  useEffect(() => {
    const elem = innerRef.current;

    if (!elem) {
      return;
    }

    const mask = IMask(elem, {
      mask: Number,
      thousandsSeparator: ",",
      radix: ".",
      scale: dp,
    });

    mask.on("complete", () => {
      if (mask.displayValue === "") {
        return onChange(null);
      }

      return onChange(mask.typedValue);
    });

    maskRef.current = mask;
    return () => {
      mask.destroy();
      maskRef.current = null;
    };
  }, [onChange, dp]);

  useEffect(() => {
    const mask = maskRef.current;
    if (!mask) {
      return;
    }

    if (Object.is(value, -0)) {
      return;
    }

    mask.value = value?.toString() || "";
    mask.updateValue();
  }, [value]);

  return (
    <FormGroup
      label={label}
      htmlFor={name}
      error={fieldState.error?.message}
      required={required}
    >
      <Input
        ref={(ref) => {
          innerRef.current = ref;
          fieldRef(ref);
        }}
        {...register}
        {...rest}
      />
    </FormGroup>
  );
}
